import React, { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ChoiceWheel from '../components/shared/ChoiceWheel';
import { useConfigurationItems } from './ConfigurationPage';
import { PAYROLL_ENABLED, PAYROLL_PERMISSION } from '../config/payrollFeature';

export default function AdminPage() {
  const auth = useAuth();

  // Manage Roles edits permissions, which live on Employee Management.
  const canManageEmployees = ['manage_all_employees', 'manage_coached_employees', 'manage_roles']
    .some((permission) => auth.hasPermission(permission));
  const canManageHiring = auth.hasPermission('manage_hiring');
  const canManageResumes = auth.hasPermission('manage_resumes');
  const canManageRndCredits = auth.hasPermission('manage_rd_credits');
  // Close dates, Hours Breakdown, vendors and mileage rates live on their own
  // wheel; the tile shows whenever anything on it would.
  const hasConfiguration = useConfigurationItems().length > 0;
  const canManageOnboarding = auth.hasPermission('manage_onboarding');
  // Company-specific module: the flag hides the tile wholesale, the permission gates it for everyone else.
  const canManagePayroll = PAYROLL_ENABLED && auth.hasPermission(PAYROLL_PERMISSION);

  const items = useMemo(() => ([
    {
      key: 'employees',
      show: canManageEmployees,
      path: '/admin/employees',
      icon: 'bi-people',
      title: 'Employee Management',
      description: 'View and manage employee records you are permitted to access.',
    },
    {
      key: 'resumes',
      show: canManageResumes,
      path: '/admin/resumes',
      icon: 'bi-file-earmark-person',
      title: 'Manage Resumes',
      description: 'View and search all employee resumes and export to CSV or XLSX.',
    },
    {
      key: 'hiring',
      show: canManageHiring,
      path: '/admin/hiring',
      icon: 'bi-person-workspace',
      title: 'Hiring Dashboard',
      description: 'Track candidates and run onboarding actions.',
    },
    {
      key: 'onboarding',
      show: canManageOnboarding,
      path: '/admin/onboarding',
      icon: 'bi-signpost-2',
      title: 'Onboarding',
      description: 'Build the onboarding journey and see who is working through it.',
    },
    {
      key: 'payroll',
      show: canManagePayroll,
      path: '/admin/payroll',
      icon: 'bi-cash-stack',
      title: 'Payroll',
      description: 'Pull a pay period from TimeCamp and split every employee’s hours into Direct, Indirect and Fringe Benefits.',
    },
    {
      key: 'rd-credits',
      show: canManageRndCredits,
      path: '/admin/rd-credits',
      icon: 'bi-calculator',
      title: 'R&D Credits',
      description: 'Calculate total tax write-off hours for R&D related expenses from TimeCamp.',
    },
    {
      key: 'configuration',
      show: hasConfiguration,
      path: '/admin/configuration',
      icon: 'bi-gear',
      title: 'Configuration',
      description: 'Close dates, Hours Breakdown, vendors and customers, and mileage rates.',
    },
  ].filter((item) => item.show)), [
    canManageEmployees,
    canManageHiring,
    canManageResumes,
    canManageRndCredits,
    hasConfiguration,
    canManageOnboarding,
    canManagePayroll,
  ]);

  return (
    <ChoiceWheel
      items={items}
      title="Admin"
      emptyText="You do not have access to any admin tools."
    />
  );
}
